import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';
import { Database } from '../types/database';

type BookmarkInsert = Database['public']['Tables']['bookmarks']['Insert'];

export interface BookmarkData {
  id: string;
  bookmark_type: 'shop' | 'deal';
  business_id?: string;
  deal_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface UseBookmarksReturn {
  bookmarks: BookmarkData[];
  loading: boolean;
  error: string | null;
  isBookmarked: (type: 'shop' | 'deal', id: string) => boolean;
  toggleBookmark: (type: 'shop' | 'deal', id: string, notes?: string) => Promise<void>;
  removeBookmark: (bookmarkId: string) => Promise<void>;
  getBookmarksByType: (type: 'shop' | 'deal') => BookmarkData[];
  refreshBookmarks: () => Promise<void>;
}

export const useBookmarks = (): UseBookmarksReturn => {
  const { user } = useAuthStore();
  const [bookmarks, setBookmarks] = useState<BookmarkData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all bookmarks for the current user
  const fetchBookmarks = useCallback(async () => {
    if (!user) {
      setBookmarks([]);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('bookmarks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setBookmarks(data || []);
    } catch (err) {
      console.error('Error fetching bookmarks:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch bookmarks');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Check if an item is bookmarked
  const isBookmarked = useCallback((type: 'shop' | 'deal', id: string): boolean => {
    return bookmarks.some(bookmark => {
      if (type === 'shop') {
        return bookmark.bookmark_type === 'shop' && bookmark.business_id === id;
      } else {
        return bookmark.bookmark_type === 'deal' && bookmark.deal_id === id;
      }
    });
  }, [bookmarks]);

  // Toggle bookmark (add if not bookmarked, remove if bookmarked)
  const toggleBookmark = useCallback(async (type: 'shop' | 'deal', id: string, notes?: string) => {
    if (!user) {
      setError('You must be logged in to bookmark items');
      return;
    }

    try {
      setError(null);
      
      // Check if already bookmarked
      const existingBookmark = bookmarks.find(bookmark => {
        if (type === 'shop') {
          return bookmark.bookmark_type === 'shop' && bookmark.business_id === id;
        } else {
          return bookmark.bookmark_type === 'deal' && bookmark.deal_id === id;
        }
      });

      if (existingBookmark) {
        // Remove bookmark
        const { error: deleteError } = await supabase
          .from('bookmarks')
          .delete()
          .eq('id', existingBookmark.id);

        if (deleteError) throw deleteError;

        // Update local state
        setBookmarks(prev => prev.filter(b => b.id !== existingBookmark.id));
      } else {
        // Add bookmark
        const bookmarkData: BookmarkInsert = {
          user_id: user.id,
          bookmark_type: type,
          business_id: type === 'shop' ? id : null,
          deal_id: type === 'deal' ? id : null,
          notes: notes || null,
        };

        const { data, error: insertError } = await supabase
          .from('bookmarks')
          .insert(bookmarkData)
          .select()
          .single();

        if (insertError) throw insertError;

        // Update local state
        if (data) {
          setBookmarks(prev => [data, ...prev]);
        }
      }
    } catch (err) {
      console.error('Error toggling bookmark:', err);
      setError(err instanceof Error ? err.message : 'Failed to toggle bookmark');
    }
  }, [user, bookmarks]);

  // Remove specific bookmark
  const removeBookmark = useCallback(async (bookmarkId: string) => {
    if (!user) {
      setError('You must be logged in to remove bookmarks');
      return;
    }

    try {
      setError(null);
      
      const { error: deleteError } = await supabase
        .from('bookmarks')
        .delete()
        .eq('id', bookmarkId)
        .eq('user_id', user.id); // Ensure user can only delete their own bookmarks

      if (deleteError) throw deleteError;

      // Update local state
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    } catch (err) {
      console.error('Error removing bookmark:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove bookmark');
    }
  }, [user]);

  // Get bookmarks by type
  const getBookmarksByType = useCallback((type: 'shop' | 'deal'): BookmarkData[] => {
    return bookmarks.filter(bookmark => bookmark.bookmark_type === type);
  }, [bookmarks]);

  // Refresh bookmarks
  const refreshBookmarks = useCallback(async () => {
    setLoading(true);
    await fetchBookmarks();
  }, [fetchBookmarks]);

  // Initial fetch
  useEffect(() => {
    fetchBookmarks();
  }, [fetchBookmarks]);

  return {
    bookmarks,
    loading,
    error,
    isBookmarked,
    toggleBookmark,
    removeBookmark,
    getBookmarksByType,
    refreshBookmarks,
  };
};

export default useBookmarks;